import useLatest from "@-/com.npmjs.use-latest";
import { showErrorToast } from "@-/dev.improve.haniverse.components.ui.failure-toast";
import {
  useLearnCardsPathname,
  useLearnMapModeSelector,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.map-mode";
import { useEffect } from "react";
import { useLearnSummaryResolution, useLearnSummaryStart } from "#use-learn-summary-start";

/** Whether the remembered cards route is the summary or its unresolved scoped pathname. */
export function isLearnSummaryPathname(pathname: string): boolean {
  return pathname.endsWith("/learn/summary") || pathname.endsWith("/learn");
}

/**
 * Registers how leaving the map home opens the cards (`LearnMapHomeExit`). Reward collection
 * belongs to Continue Learning's handler. Once the cards have slid in, it Starts: the deck on the
 * next card, which the deck already previews beneath the map, so the card that slides in is the one that
 * opens. With nothing to Start (the day's limit, a sync holding Start) or other cards beneath the
 * home (Undo on the first card brought the learner back to it), the map goes back to those cards.
 *
 * Registered by Learn's layout outside the card mount gate, so the map can open the requested
 * deck without mounting either flashcard host before the press.
 */
export function useLearnSummaryMapHomeExit(): void {
  const isMapHome = useLearnMapModeSelector((mode) => mode.isMapHome) ?? false;
  const mapHomeExitRef = useLearnMapModeSelector((mode) => mode.mapHomeExitRef);
  const isSummaryBeneath = isLearnSummaryPathname(useLearnCardsPathname());
  const resolution = useLearnSummaryResolution({ enabled: isMapHome });
  const { start } = useLearnSummaryStart({ resolution, source: "map_home" });
  const startRef = useLatest(isSummaryBeneath ? start : null);
  useEffect(() => {
    if (!isMapHome || mapHomeExitRef === undefined) {
      return;
    }
    const exit = () => {
      const startDeck = startRef.current;
      return (back: () => void) => {
        if (startDeck === null) {
          back();
          return;
        }
        // A deck that never opens leaves the route on the map, which slides back over the cards.
        startDeck().catch((error: unknown) => showErrorToast("Could not open the cards", error));
      };
    };
    mapHomeExitRef.current = exit;
    return () => {
      if (mapHomeExitRef.current === exit) {
        mapHomeExitRef.current = null;
      }
    };
  }, [isMapHome, mapHomeExitRef, startRef]);
}
